import qs from 'query-string';
import axios from 'axios';
import type { WebAuth, AuthOptions } from 'auth0-js';
import { config } from './env.config';
import { executeWithDeferredLibrary } from './utils';
import { resolveAuthDomainFromFlag } from './authDomainFlag';
import { addAction } from '@shared/utils/logger';
import { importWithRetry } from '@shared/core/chunkRetry';
import type { AuthAuthorizeRedirect, AuthAuthorizeResult, AuthSignupArgs, AuthCallback, AuthError } from './auth.types';

// Re-exported under `Auth0`-prefixed names for back-compat with existing
// imports from `@static/js/auth0` (and `@static/js/joy`, which forwards them).
export type { AuthAuthorizeRedirect as Auth0AuthorizeRedirect, AuthAuthorizeResult as Auth0AuthorizeResult, AuthSignupArgs as Auth0SignupArgs } from './auth.types';

const CLIENT_ID = config.auth0ClientId;
const REDIRECT_URI = `${config.clientUri}/account-callback`;
const CUSTOM_DOMAIN = 'accounts.withjoy.com';
const AUTH0_DOMAIN = 'withjoy.auth0.com';
const IDENTITY_SERVER_URI = `${config.serverUri}/identity`;
/**
 * Currently we are using 2 domains for auth0. `withjoy.auth0.com` is the one provided by auth0 and
 * joy custom domain `accounts.withjoy.com`.
 * The reason we need both is because the legacy monorepo uses the one provided by auth0 and we are
 * very reluctant to modifying stuff there because of legacy code and apis being used.
 * The custom domain we need because our it's a necessity to have a tailored experience for our users
 * when they sign up so we must use a custom domain to avoid issues with 3rd party cookies Safari and
 * probably Chrome very soon.
 *
 * !!! So for now, unless it is sign up or login, any auth0 action should use `withjoy.auth0.com`
 */
// Two independent deferreds so login/signup are NOT gated on the flag fetch. `customDomainWebAuth`
// resolves as soon as auth0-js loads; `webAuth` waits on the (bounded) flag resolution.
const [resolveCustomDomainWebAuth, executeWithCustomDomainWebAuth] = executeWithDeferredLibrary<WebAuth>();
const [resolveWebAuth, executeWithWebAuth] = executeWithDeferredLibrary<WebAuth>();

// The host `webAuth` was actually built with, captured for telemetry. Read from here rather than off
// the WebAuth instance so we don't depend on auth0-js internals (`baseOptions.domain`).
let resolvedWebAuthDomain: string | null = null;

/**
 * Fire-and-forget record of which auth flow ran on which host.
 *
 * `authDomainResolved` (authDomainFlag.ts) reports what the flag resolved to once per page load;
 * this reports what was actually *invoked*. That matters because `authorize`, social login and
 * logout are full-page redirects — New Relic never sees them as AjaxRequests, so without this
 * they're invisible. Only `changePassword` shows up on its own (it's an XHR).
 *
 * Hostname and flow name only — no PII, no await.
 */
const trackAuthFlow = (flow: string, domain: string | null) => {
  addAction('authFlowInvoked', { flow, domain: domain ?? 'unresolved' });
};

export const initAuth0 = () => {
  // auth0-js is a browser library and as such, is not instantiated on the server during SSR.
  // Normal imports at the top of the file would result in a error log.
  // https://github.com/auth0/auth0.js/issues/877#issuecomment-665743985
  // Removing the magic webpackChunkName because it's interfering with
  // webpack's internal chunking algorithm.
  return importWithRetry(() => import('auth0-js'), 'auth0-js').then(({ WebAuth }) => {
    const config: AuthOptions = {
      // login + signup use auth0-js embedded auth (`/co/authenticate`, `/dbconnections/signup`),
      // which the auth proxy does NOT implement — so they stay on the Auth0 custom domain.
      // `audience` is an Auth0 API identifier (not a network host) and is left unchanged.
      domain: CUSTOM_DOMAIN,
      clientID: CLIENT_ID,
      redirectUri: REDIRECT_URI,
      audience: 'https://withjoy.auth0.com/userinfo',
      responseType: 'code',
      scope: 'openid offline_access',
    };
    // login + signup: build immediately so they never wait on (or pay the timeout of) the flag fetch.
    resolveCustomDomainWebAuth(new WebAuth(config));

    // social login, authorize, changePassword and logout use redirect / `/dbconnections/change_password`
    // / `/v2/logout`, which the auth proxy implements. Only THIS instance's host comes from the
    // `auth-domain-joy-web-social-logout` flag, falling back to today's Auth0 host when the flag is
    // unavailable (no behavior change). `executeWithWebAuth` queues those actions until it resolves,
    // so none are dropped or raced; the second `.then` arg guarantees it's built even on rejection.
    const buildWebAuth = (domain: string) => {
      resolvedWebAuthDomain = domain;
      resolveWebAuth(new WebAuth({ ...config, domain }));
    };
    resolveAuthDomainFromFlag().then(
      flagDomain => buildWebAuth(flagDomain ?? AUTH0_DOMAIN),
      () => buildWebAuth(AUTH0_DOMAIN),
    );
  });
};

const socialLogin = (connection: string, redirect: AuthAuthorizeRedirect) => {
  executeWithWebAuth(webAuth => {
    trackAuthFlow('socialLogin', resolvedWebAuthDomain);
    webAuth.authorize({ connection, redirectUri: REDIRECT_URI, state: JSON.stringify(redirect) });
  });
};

const authorize = (params?: Record<string, string | boolean | number>) => {
  executeWithWebAuth(webAuth => {
    trackAuthFlow('authorize', resolvedWebAuthDomain);
    webAuth.authorize({ redirectUri: REDIRECT_URI, ...params });
  });
};

const deriveAuthorizeResult = (urlSearch: string): AuthAuthorizeResult => {
  const parsedUrl = qs.parse(urlSearch);
  const code = parsedUrl['code'] as string;
  const redirectJSON = parsedUrl['state'] as string;
  return { code, redirectJSON };
};

const hydrateAuthorizeRedirect = (state: string): AuthAuthorizeRedirect => {
  // the caller must `try { } catch { }` as needed
  return JSON.parse(state);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const login = (email: string, password: string, cb: AuthCallback<any, AuthError>) => {
  executeWithCustomDomainWebAuth(customDomainWebAuth => {
    // Recorded for completeness, so every auth flow shows up in one place during the ramp.
    // NB: this logs the constant, not the host the client was actually built with — unlike the
    // proxied flows above, which log `resolvedWebAuthDomain`. login/signup take no flag input, so
    // there is nothing to vary; treat this as "the flow ran", not as proof it stayed put.
    trackAuthFlow('login', CUSTOM_DOMAIN);
    customDomainWebAuth.login({ realm: 'Username-Password-Authentication', email, password }, cb);
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const signup = (args: AuthSignupArgs, cb: AuthCallback<any, AuthError>) => {
  executeWithCustomDomainWebAuth(customDomainWebAuth => {
    trackAuthFlow('signup', CUSTOM_DOMAIN);
    customDomainWebAuth.signup({ connection: 'Username-Password-Authentication', ...args }, cb);
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const changePassword = (email: string, cb: AuthCallback<any, AuthError>) => {
  executeWithWebAuth(webAuth => {
    trackAuthFlow('changePassword', resolvedWebAuthDomain);
    // Wrap the caller's callback to record the outcome. This is the one flow whose result we can
    // observe client-side (the others are redirects that navigate away), and it's the flow that has
    // never been exercised against the proxy — so a failure here should be loud, not silent.
    webAuth.changePassword({ connection: 'Username-Password-Authentication', email }, (err, res) => {
      const failure = err as { code?: string; name?: string; statusCode?: number } | null | undefined;
      addAction('authFlowResult', {
        flow: 'changePassword',
        domain: resolvedWebAuthDomain ?? 'unresolved',
        ok: !err,
        // Error shape only — no message body, so no PII. `statusCode` is the field that matters:
        // a real HTTP response (400/401/429/5xx) means the request reached the host, whereas 0 or
        // absent means it never completed — a CORS rejection or network failure. Those two look
        // identical from the outside and are the first thing to check when this flow breaks
        // against a new host.
        ...(err ? { errorCode: failure?.code ?? failure?.name ?? 'unknown', statusCode: failure?.statusCode ?? 0 } : {}),
      });
      cb(err, res);
    });
  });
};

const signout = (returnToUrl?: string) => {
  executeWithWebAuth(webAuth => {
    trackAuthFlow('signout', resolvedWebAuthDomain);
    webAuth.logout({ returnTo: returnToUrl });
  });
};

const refreshToken = (code: string, shouldSkipTokenDelegation = false) => {
  return axios.post(`${IDENTITY_SERVER_URI}/refreshToken`, { redirectUri: REDIRECT_URI, code, skipDelegate: shouldSkipTokenDelegation });
};

export const auth0 = {
  authorize,
  socialLogin,
  deriveAuthorizeResult,
  hydrateAuthorizeRedirect,
  login,
  signup,
  signout,
  changePassword,
  refreshToken,
};

export type Auth0 = typeof auth0;
